import { useState, useEffect, useCallback } from 'react';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { followService } from '../services/followService';

export function useUserProfile(userId: string | null) {
  const [profile, setProfile] = useState<UserPublicProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [followLoading, setFollowLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      setProfile(await buddyService.getUserProfile(userId));
    } catch {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const follow = useCallback(async () => {
    if (!userId) return;
    setFollowLoading(true);
    try {
      await followService.follow(userId);
      setProfile(prev => prev ? {
        ...prev,
        isFollowing: true,
        followerCount: prev.followerCount + 1,
      } : prev);
    } finally {
      setFollowLoading(false);
    }
  }, [userId]);

  const unfollow = useCallback(async () => {
    if (!userId) return;
    setFollowLoading(true);
    try {
      await followService.unfollow(userId);
      setProfile(prev => prev ? {
        ...prev,
        isFollowing: false,
        followerCount: Math.max(0, prev.followerCount - 1),
      } : prev);
    } finally {
      setFollowLoading(false);
    }
  }, [userId]);

  return { profile, loading, error, refresh, follow, unfollow, followLoading };
}
